import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type { InventoryQueryAllSchema, InventoryQuerySchema } from './schemas/inventory';
import type {
  InventoryKitCreateSchema,
  InventoryKitQueryAllSchema,
  InventoryKitQuerySchema,
  InventoryKitUpdateSchema,
} from './schemas/inventory-kit';
import type {
  InventoryKitItemCreateMutationSchema,
  InventoryKitItemMutationSchema,
  InventoryKitItemQuerySchema,
} from './schemas/inventory-kit-item';

export const useGetInventoryKitWithItemsOnlyMutation: UseControlledMutation<InventoryKitQuerySchema> = () =>
  useMutation({
    mutationFn: async ({ search, per_page, include }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORY_KIT_WITH_ITEMS_ONLY_ASYNC', { search, per_page, include }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryKitQueryAllSchema>({
            endpoint: '/api/v1/inventory-kits?filter[has_items]=true',
            params: { search, per_page, include },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useGetInventoryKitMutation: UseControlledMutation<InventoryKitQuerySchema> = () =>
  useMutation({
    mutationFn: async ({ search, per_page, include }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORY_KIT_ASYNC', { search, per_page, include }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryKitQueryAllSchema>({
            endpoint: '/api/v1/inventory-kits',
            params: { search, per_page, include },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetInventoryKits(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_KITS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<InventoryKitQueryAllSchema>({
        endpoint:
          '/api/v1/inventory-kits?include=logo,inventory_kit_items.inventory.inventory_category,inventory_kit_items.inventory.logo,category',
        params: `${apiParamStr}`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INVENTORY_KITS'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, InventoryKitCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/inventory-kits/',
        payload,
      }),
  });

export function useGetInventoryKit(id: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_KIT', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InventoryKitQuerySchema }>({
        endpoint: `/api/v1/inventory-kits/${id}`,
        params: {
          include: 'logo,inventory_kit_items.inventory.inventory_category,inventory_kit_items.inventory.logo,category',
        },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InventoryKitQuerySchema }, ApiError, Partial<InventoryKitUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-kits/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVENTORY_KITS_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-kits/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });

export const useGetInventoriesMutation: UseControlledMutation<InventoryQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page, ...rest }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORIES_ASYNC', { search, per_page, ...rest }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryQueryAllSchema>({
            endpoint: '/api/v1/inventories',
            params: { search, per_page, include: 'logo,inventory_category', ...rest },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useInventoryKitItemsCreateMutation = () =>
  useMutation<{ data: InventoryKitItemQuerySchema }, ApiError, InventoryKitItemCreateMutationSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-kit-items`,
        method: 'POST',
        payload,
      }),
  });

export const useInventoryKitItemsBulkUpdateMutation = () =>
  useMutation<{ data: InventoryKitItemQuerySchema }, ApiError, { items: InventoryKitItemMutationSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-kit-items/bulk-update`,
        method: 'POST',
        payload,
      }),
  });
